// ─────────────────────────────────────────────────────────────────
// Glass-i — HR Departments helpers (Stage HRMS-1). Pure JS — tested
// by the frontend node:test runner.
// ─────────────────────────────────────────────────────────────────

let _rowIdCounter = 0;
function nextRowId() { _rowIdCounter += 1; return `hd-c-${_rowIdCounter}`; }

export function normalizeDepartmentRows(rows) {
  if (!Array.isArray(rows)) return [];
  return rows.map((r) => ({
    _clientRowId:   r._clientRowId || (r.id ? `hd-${r.id}` : nextRowId()),
    _dirty:         false,
    id:             r.id || null,
    departmentCode: r.departmentCode || '',
    departmentName: r.departmentName || '',
    description:    r.description || '',
    active:         r.active !== false
  }));
}

export function markDirtyRow(rows, clientRowId, patch) {
  if (!Array.isArray(rows)) return [];
  return rows.map((r) => r._clientRowId === clientRowId ? { ...r, ...patch, _dirty: true } : r);
}

export function sortDepartments(rows) {
  if (!Array.isArray(rows)) return [];
  return [...rows].sort((a, b) =>
    String((a && a.departmentName) || '').localeCompare(String((b && b.departmentName) || '')));
}

function trimOrNull(v) {
  if (v === null || v === undefined) return null;
  const s = String(v).trim();
  return s ? s : null;
}

export function buildDepartmentPayload(row) {
  if (!row || typeof row !== 'object') return {};
  return {
    departmentCode: trimOrNull(row.departmentCode),
    departmentName: trimOrNull(row.departmentName),
    description:    trimOrNull(row.description),
    active:         row.active !== false
  };
}

// Emits { rows, deletes } for the /bulk-save endpoint — only dirty
// rows + queued deletes go on the wire.
export function buildDepartmentBulkPayload(rows, deletedIds = []) {
  const dirty = (rows || []).filter((r) => r && r._dirty);
  return {
    rows: dirty.map((r) => {
      const out = buildDepartmentPayload(r);
      if (r.id) out.id = r.id;
      if (r._clientRowId) out._clientRowId = r._clientRowId;
      return out;
    }),
    deletes: Array.isArray(deletedIds)
      ? deletedIds.filter((id) => typeof id === 'string' && id.length > 0) : []
  };
}

export function validateDepartmentRow(row, allRows = []) {
  const errors = [];
  if (!row || typeof row !== 'object') {
    return { ok: false, errors: [{ field: 'row', message: 'Row is required.' }] };
  }
  const code = String(row.departmentCode || '').trim();
  const name = String(row.departmentName || '').trim();
  if (!code) errors.push({ field: 'departmentCode', message: 'Department Code is required.', clientRowId: row._clientRowId });
  if (!name) errors.push({ field: 'departmentName', message: 'Department Name is required.', clientRowId: row._clientRowId });
  if (Array.isArray(allRows) && allRows.length > 0) {
    const peers = allRows.filter((r) => r && r._clientRowId !== row._clientRowId);
    if (code) {
      const lc = code.toLowerCase();
      if (peers.some((p) => String(p.departmentCode || '').trim().toLowerCase() === lc)) {
        errors.push({ field: 'departmentCode', message: `Department Code "${code}" already exists.`, clientRowId: row._clientRowId });
      }
    }
    if (name) {
      const ln = name.toLowerCase();
      if (peers.some((p) => String(p.departmentName || '').trim().toLowerCase() === ln)) {
        errors.push({ field: 'departmentName', message: `Department Name "${name}" already exists.`, clientRowId: row._clientRowId });
      }
    }
  }
  return { ok: errors.length === 0, errors };
}

export function validateDepartmentRows(rows) {
  const all = Array.isArray(rows) ? rows : [];
  const errors = [];
  for (const r of all) {
    const { ok, errors: rowErrors } = validateDepartmentRow(r, all);
    if (!ok) errors.push(...rowErrors);
  }
  return { ok: errors.length === 0, errors };
}
